import { useQuery } from "@tanstack/react-query";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import { DEFAULT_SUBMIT_SHORTCUT, type SubmitShortcut } from "@rivets/core";

/** Only the fields a submit decision needs, so the helper can be tested with a
 * plain object rather than a synthetic React event. */
export type SubmitKeyEvent = {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey?: boolean;
  nativeEvent?: { isComposing?: boolean };
};

/**
 * Whether this Return submits rather than types a newline.
 *
 * Command-Return (and Control-Return, for keyboards without Command) submits
 * under both settings — it was the only chord Rivets ever had. What the setting
 * changes is what a bare Return does: send, or start a new line. Shift-Return
 * is always a newline, and a Return that is closing an IME composition is
 * always the composition's, never ours.
 */
export function isSubmitKeyPress(event: SubmitKeyEvent, shortcut: SubmitShortcut): boolean {
  if (event.key !== "Enter" || event.nativeEvent?.isComposing) return false;
  if (event.metaKey || event.ctrlKey) return true;
  return shortcut === "enter" && !event.shiftKey && !event.altKey;
}

/** The chord label for a tooltip: "Return" or "Command-Return". */
export function submitShortcutLabel(shortcut: SubmitShortcut): string {
  return shortcut === "enter" ? "Return" : "Command-Return";
}

/**
 * The account's send-key setting, read from the bootstrap `/api/me` response
 * every authenticated screen already loads. Falls back to the contract default
 * while that request is in flight: a composer must always know what Return does.
 */
export function useSubmitShortcut(): SubmitShortcut {
  const { data } = useQuery({ queryKey: qk.me(), queryFn: () => client.me() });
  return data?.preferences.submitShortcut ?? DEFAULT_SUBMIT_SHORTCUT;
}
